import React, { useState, useEffect, memo, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../supabase/config';
import { logger } from '../utils/logger';

const ParentControls = memo(({ child, onUpdate }) => {
  const { user, updateUserProfile } = useUser();
  const [parentControlsEnabled, setParentControlsEnabled] = useState(
    child?.parent_controls_enabled ?? true
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setParentControlsEnabled(child?.parent_controls_enabled ?? true);
  }, [child]);

  const handleToggleControls = useCallback(async () => {
    if (!child?.id || !user?.id) return;

    setIsUpdating(true);
    setError(null);

    try {
      const newValue = !parentControlsEnabled;
      
      // Update in database
      const { error: updateError } = await supabase
        .from('users')
        .update({ parent_controls_enabled: newValue })
        .eq('id', child.id);

      if (updateError) {
        throw updateError;
      }

      setParentControlsEnabled(newValue);
      
      if (onUpdate) {
        onUpdate({ ...child, parent_controls_enabled: newValue });
      }

      logger.info('Parent controls updated:', { childId: child.id, enabled: newValue });
    } catch (err) {
      logger.error('Error updating parent controls:', err);
      setError(err.message || 'Failed to update parent controls');
    } finally {
      setIsUpdating(false);
    }
  }, [child, user, parentControlsEnabled, onUpdate]);

  if (!child) {
    return (
      <div className="text-center py-8 text-gray-500">
        Select a child to manage their settings
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <span>⚙️</span>
        <span>Parent Controls</span>
      </h3>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {/* Parent Controls Toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <div className="font-medium text-gray-900 mb-1">
              Parent Control Access
            </div>
            <div className="text-sm text-gray-600">
              {parentControlsEnabled
                ? 'You can manage this child\'s account settings and preferences'
                : 'This child manages their own settings. You have view-only access.'}
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={parentControlsEnabled}
              onChange={handleToggleControls}
              disabled={isUpdating}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>

        {parentControlsEnabled && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-blue-600 mr-2">ℹ️</span>
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">With parent controls enabled, you can:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Set learning goals</li>
                  <li>Manage difficulty settings</li>
                  <li>Control sound and music preferences</li>
                  <li>View detailed progress reports</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {!parentControlsEnabled && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-yellow-600 mr-2">⚠️</span>
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">Parent controls are disabled</p>
                <p>You can view this child's progress but cannot modify their settings.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ParentControls.displayName = 'ParentControls';

export default ParentControls;

